import { Route, Switch, useLocation } from 'wouter';
import { AuthProvider, useAuth } from './lib/auth';
import { LoginPage } from './pages/Login';
import { ProjectsListPage } from './pages/ProjectsList';
import { DashboardPage } from './pages/Dashboard';
import { BrandPage } from './pages/Brand';
import { PipelinePage } from './pages/Pipeline';
import { DraftsPage } from './pages/Drafts';
import { DraftDetailPage } from './pages/DraftDetail';
import { AdminTenantsPage } from './pages/AdminTenants';
import { TeamPage } from './pages/Team';
import { SettingsPage } from './pages/Settings';
import { AppShell } from './components/AppShell';

export function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}

function Gate() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background-dark text-muted font-mono text-xs uppercase tracking-wider">
        Opening Bubbles…
      </main>
    );
  }

  if (!user) return <LoginPage />;

  return <Shell />;
}

/**
 * Brand workspace routes are deliberately phase-shaped:
 * Setup teaches the brand, Generator runs the graph, Posts holds publishable outputs.
 * Legacy routes still render the same pages so old links do not strand users.
 */
function Shell() {
  const [location] = useLocation();
  const projectId = projectIdFromLocation(location);

  return (
    <AppShell projectId={projectId}>
      <Switch>
        <Route path="/" component={ProjectsListPage} />
        <Route path="/projects/:id" component={DashboardPage} />
        <Route path="/projects/:id/setup" component={BrandPage} />
        <Route path="/projects/:id/brand" component={BrandPage} />
        <Route path="/projects/:id/generator" component={PipelinePage} />
        <Route path="/projects/:id/pipeline" component={PipelinePage} />
        <Route path="/projects/:id/posts" component={DraftsPage} />
        <Route path="/projects/:id/drafts" component={DraftsPage} />
        <Route path="/projects/:id/posts/:draftId" component={DraftDetailPage} />
        <Route path="/projects/:id/drafts/:draftId" component={DraftDetailPage} />
        <Route path="/projects/:id/team" component={TeamPage} />
        <Route path="/projects/:id/settings" component={SettingsPage} />
        <Route path="/admin">
          <AdminGate />
        </Route>
        <Route>
          <NotFound />
        </Route>
      </Switch>
    </AppShell>
  );
}

function projectIdFromLocation(location: string): string | undefined {
  const match = location.match(/^\/projects\/([^/]+)/);
  return match ? decodeURIComponent(match[1]!) : undefined;
}

function AdminGate() {
  const { user } = useAuth();
  return user?.isAdmin ? <AdminTenantsPage /> : <NotFound />;
}

function NotFound() {
  const [, navigate] = useLocation();
  return (
    <main className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-muted font-mono text-xs uppercase tracking-wider">
      <p>This room is not wired yet.</p>
      <button onClick={() => navigate('/')} className="btn-bracket border border-border-color px-3 py-1.5 hover:text-text-primary hover:border-text-primary">
        Back to brands
      </button>
    </main>
  );
}
